import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  GamificationDashboardAnalytics,
  gamificationApi,
} from "api/admin/gamification";
import { useQuery } from "react-query";
import Link from "components/admin/AdminLink";
import {
  SectionCard,
  StatusPill,
  formatLabel,
} from "./components";

export default function Overview() {
  const { can } = useAdminAccess();
  const actions = useQuery("gamification-actions-overview", gamificationApi.listActions, { enabled: can("gamification.actions.view"),
    retry: false,
  });
  const tiers = useQuery("gamification-tiers-overview", gamificationApi.listTiers, { enabled: can("gamification.eloTiers.view"),
    retry: false,
  });
  const rewards = useQuery(
    "gamification-reward-definitions",
    gamificationApi.listRewardDefinitions,
    { enabled: can("gamification.rewardDefinitions.view"), retry: false },
  );
  const fulfilment = useQuery(
    "gamification-fulfilment-overview",
    () => gamificationApi.listFulfilmentTasks({ status: "pending", limit: 1 }),
    { enabled: can("gamification.fulfilment.view"), retry: false },
  );
  const policy = useQuery(
    "gamification-policy-overview",
    gamificationApi.getStreakPolicy,
    { enabled: can("gamification.streaks.view"), retry: false },
  );
  const milestones = useQuery(
    "gamification-streak-milestones",
    gamificationApi.listMilestones,
    { enabled: can("gamification.streaks.view"), retry: false },
  );
  const dashboard = useQuery(
    "gamification-dashboard",
    gamificationApi.getDashboard,
    { enabled: can("gamification.overview.view"), retry: false },
  );

  return (
    <div className="space-y-6">
      <AdminPermission
        permission={[
          "gamification.eloTiers.view",
          "gamification.actions.view",
          "gamification.streaks.view",
          "gamification.rewardDefinitions.view",
          "gamification.fulfilment.view",
        ]}
      >
        <SectionCard
          title="What would you like to manage?"
          description="Choose an area. Advanced scheduling and audit details stay out of the way until you need them."
        >
          <div className="divide-y divide-gray-100">
            <AdminPermission permission={"gamification.eloTiers.view"}>
              <OverviewRow
                icon="fa-layer-group"
                title="ELO points and tiers"
                body={`${tiers.data?.tiers.length ?? "—"} tiers with configurable rewards`}
                to="/gamification/progression"
                action="Manage ELO & tiers"
              />
            </AdminPermission>
            <AdminPermission permission={"gamification.actions.view"}>
              <OverviewRow
                icon="fa-bolt"
                title="Actions"
                body={`${actions.data?.actions.length ?? "—"} actions with ELO points and streak categories`}
                to="/gamification/actions"
                action="Manage actions"
              />
            </AdminPermission>
            <AdminPermission permission={"gamification.streaks.view"}>
              <OverviewRow
                icon="fa-fire"
                title="Streak & milestones"
                body={`Monday–Sunday calendar weeks · ${milestones.data?.milestones.length ?? "—"} milestones`}
                to="/gamification/streaks"
                action="Manage streaks"
              />
            </AdminPermission>
            <AdminPermission permission={"gamification.rewardDefinitions.view"}>
              <OverviewRow
                icon="fa-gift"
                title="Rewards"
                body={`${rewards.data?.rewards.filter((item) => item.status === "active").length ?? "—"} active reusable rewards`}
                to="/gamification/rewards"
                action="Manage rewards"
              />
            </AdminPermission>
            <AdminPermission permission={"gamification.fulfilment.view"}>
              <OverviewRow
                icon="fa-list-check"
                title="Pending rewards"
                body={`${fulfilment.data?.total ?? "—"} manual rewards need attention`}
                to="/gamification/fulfilment"
                action="Open pending rewards"
                attention={Boolean(fulfilment.data?.total)}
              />
            </AdminPermission>
          </div>
        </SectionCard>
      </AdminPermission>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Members with ELO", dashboard.data?.analytics.activeEloMembers],
          ["Active streaks", dashboard.data?.analytics.streakHealth.active],
          ["Streaks at risk", dashboard.data?.analytics.streakHealth.atRisk],
          ["ELO awarded", dashboard.data?.analytics.elo.pointsAwarded],
          ["Open fulfilment", dashboard.data?.analytics.pendingFulfilment],
        ].map(([label, value]) => (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-4" key={String(label)}>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-2 text-2xl font-semibold text-gray-950">{value ?? "—"}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <TierDistribution analytics={dashboard.data?.analytics} />
        <SectionCard
          title="Streak health"
          description="Users who may need a nudge before their current streak deadline."
        >
          <div className="grid divide-y divide-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <Metric label="Active" value={dashboard.data?.analytics.streakHealth.active} />
            <Metric label="At risk" value={dashboard.data?.analytics.streakHealth.atRisk} attention />
            <Metric
              label="Shields used this month"
              value={dashboard.data?.analytics.streakHealth.shieldsUsedThisMonth}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="ELO activity"
          description="All-time ELO awards by action category."
        >
          <CategoryBreakdown categories={dashboard.data?.analytics.awardsByCategory} />
        </SectionCard>
        <SectionCard
          title="Reward health"
          description="Issued rewards and the work still waiting on the operations team."
        >
          <div className="grid divide-y divide-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <Metric label="Issued" value={dashboard.data?.analytics.grantsIssued} />
            <Metric label="Applied" value={dashboard.data?.analytics.rewardHealth.appliedGrants} />
            <Metric label="Reward uses" value={dashboard.data?.analytics.rewardHealth.usesConsumed} />
          </div>
          <div className="border-t border-gray-100 px-5 py-4 text-sm text-gray-600">
            {dashboard.data?.analytics.overdueFulfilment ?? "—"} overdue fulfilment task
            {dashboard.data?.analytics.overdueFulfilment === 1 ? "" : "s"}
          </div>
        </SectionCard>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
        <AdminPermission permission={"gamification.streaks.view"}>
          <div className="flex items-center gap-3">
            <StatusPill status={policy.data?.policy ? "active" : "draft"} />
            <p className="text-sm text-gray-600">
              {policy.data?.policy
                ? "Gamification rules are active."
                : "The fixed Monday–Sunday streak rule is active."}
            </p>
          </div>
        </AdminPermission>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  attention = false,
}: {
  label: string;
  value?: number;
  attention?: boolean;
}) {
  return (
    <div className="px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${attention && value ? "text-orange-700" : "text-gray-950"}`}>
        {value ?? "—"}
      </p>
    </div>
  );
}

function TierDistribution({
  analytics,
}: {
  analytics?: GamificationDashboardAnalytics;
}) {
  const tiers = analytics?.tierDistribution.tiers ?? [];
  const membersInTiers = tiers.reduce((total, tier) => total + tier.members, 0);

  return (
    <SectionCard
      title="Tier distribution"
      description="Current ELO members by managed tier."
    >
      <div className="divide-y divide-gray-100">
        {tiers.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500">No tier members yet.</p>
        ) : (
          tiers.map((tier) => {
            const share = membersInTiers > 0 ? (tier.members / membersInTiers) * 100 : 0;
            return (
              <div className="px-5 py-3" key={tier.id}>
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-medium text-gray-800">{tier.name}</span>
                  <span className="font-semibold text-gray-950">{tier.members}</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-orange-500" style={{ width: `${share}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="grid border-t border-gray-100 sm:grid-cols-3">
        <Metric label="Without tier" value={analytics?.tierDistribution.withoutTierMembers} />
        <Metric label="In grace" value={analytics?.tierDistribution.graceMembers} attention />
        <Metric label="Frozen" value={analytics?.tierDistribution.frozenMembers} />
      </div>
    </SectionCard>
  );
}

function CategoryBreakdown({ categories = {} }: { categories?: Record<string, number> }) {
  const entries = Object.entries(categories);
  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  if (entries.length === 0) {
    return <p className="px-5 py-8 text-sm text-gray-500">No ELO has been awarded yet.</p>;
  }

  return (
    <div className="divide-y divide-gray-100">
      {entries.map(([category, count]) => (
        <div className="flex items-center justify-between gap-4 px-5 py-3 text-sm" key={category}>
          <span className="text-gray-600">{formatLabel(category)}</span>
          <span className="font-semibold text-gray-950">
            {count} <span className="font-normal text-gray-400">({total ? Math.round((count / total) * 100) : 0}%)</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function OverviewRow({
  icon,
  title,
  body,
  to,
  action,
  attention,
}: {
  icon: string;
  title: string;
  body: string;
  to: string;
  action: string;
  attention?: boolean;
}) {
  return (
    <Link
      className="group flex flex-col gap-4 px-5 py-5 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-400 sm:flex-row sm:items-center"
      to={to}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          attention ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-700"
        }`}
      >
        <i className={`fa ${icon}`} />
      </span>
      <div className="min-w-0 flex-1 text-left">
        <h3 className="text-left text-base font-semibold text-gray-950">{title}</h3>
        <p className="mt-1 text-left text-sm text-gray-500">{body}</p>
      </div>
      <span className="text-sm font-semibold text-gray-700 group-hover:text-orange-700">
        {action}
        <i className="fa fa-arrow-right ml-2 text-xs" />
      </span>
    </Link>
  );
}
